<template>
  <form @submit.prevent="onSubmit">
    <v-card>
      <v-card-title v-if="create">
        <h1>New Combo</h1>
      </v-card-title>

      <v-card-text v-if="loading">
        <div  class="d-flex justify-center align-center">
          <v-progress-circular :size="50" indeterminate color="primary"></v-progress-circular>
        </div>
      </v-card-text>

      <v-card-text v-if="!loading">
        <v-container fluid grid-list-md>
          <v-layout row class="product--layout">
            <v-flex xs12 sm6 md4>
              <product-image :src="productThumbnail"></product-image>
              <v-btn block color="teal" dark @click="openThumbnailFileDialog">
                <v-icon left>mdi-upload</v-icon>Upload Thumbnail
              </v-btn>
              <input v-show="false" type="file" accept="image/png" ref="thumbnailFileDialog" @change="displayThumbnail" >
            </v-flex>

            <v-flex xs12 sm6 md8>
              <v-layout row wrap class="layout-wrap-push-top">
                <v-flex
                  xs12 sm12 md6 lg4
                  v-for="field in fields"
                  :key="field.key"
                >
                  <template v-if="field.type === 'text'">
                    <v-text-field
                      :label="field.label"
                      :value="getProperty(product, field.key)"
                      @change="updateProperty(product, field.key, $event)"
                      :required="field.required || false"
                      :loading="field.loading"
                      :error="field.error.length > 0"
                      :error-messages="field.error"
                      :placeholder="field.placeholder || null"
                      :readonly="field.readonly"
                    ></v-text-field>
                  </template>

                  <template v-if="field.type === 'number'">
                    <v-text-field
                      :label="field.label"
                      :value="getProperty(product, field.key)"
                      @change="updateProperty(product, field.key, $event)"
                      :required="field.required || false"
                      :loading="field.loading"
                      :type="'number'"
                      :step="'any'"
                      :error="field.error.length > 0"
                      :error-messages="field.error"
                      :placeholder="field.placeholder || null"
                    ></v-text-field>
                  </template>

                  <template v-if="field.type === 'rich-text'">
                    <v-text-field
                      :label="field.label"
                      @click="onDescriptionFocus(field.key)"
                      :value="getProperty(product, field.key)"
                      :loading="field.loading"
                      :error="field.error.length > 0"
                      :error-messages="field.error"
                      readonly
                    ></v-text-field>
                  </template>

                  <template v-if="field.type === 'select'">
                    <v-autocomplete
                      :items="field.items"
                      :label="field.label"
                      :value="getProperty(product, field.key)"
                      @input="updateProperty(product, field.key, $event)"
                      :required="field.required"
                      :loading="field.loading"
                      :error="field.error.length > 0"
                      :error-messages="field.error"
                      :readonly="field.readonly"
                      persistent-hint
                      :multiple="field.multiple"
                    ></v-autocomplete>
                  </template>
                </v-flex>

                <v-flex xs1 class="text-xs-center">
                  <v-btn icon color="blue" dark fab small @click="addCombo" :disabled="disableProductSelect">
                    <v-icon>add_circle</v-icon>
                  </v-btn>
                </v-flex>
              </v-layout>

              <transition-group name="flip-list" tag="div">
                <v-layout row wrap class="layout-wrap-push-top" v-for="(combo, index) in product.combos" :key="combo.id">
                  <v-flex xs7>
                    <v-autocomplete
                      v-model="combo.product_id"
                      :disabled="disableProductSelect"
                      :items="selectableProducts"
                      color="blue-grey lighten-2"
                      :label="'Product ' + (index + 1)"
                      item-text="name"
                      item-value="id"
                      @change="updateCostAndRearrangeCombosOrder"
                    >
                      <template
                        slot="selection"
                        slot-scope="data"
                      >
                        <v-chip
                          :selected="data.selected"
                          close
                          class="chip--select-multi"
                          @input="data.parent.selectItem(data.item)"
                          small
                        >
                          <v-avatar>
                            <img class="full-look" :style="{backgroundImage:'url('+data.item.src+')'}">
                          </v-avatar>
                          {{ data.item.name_zh_hk }}
                        </v-chip>
                      </template>
                      <template
                        slot="item"
                        slot-scope="data"
                      >
                        <template v-if="typeof data.item !== 'object'">
                          <v-list-tile-content v-text="data.item"></v-list-tile-content>
                        </template>
                        <template v-else>
                          <v-list-tile-avatar tile>
                            <img class="full-look" :style="{backgroundImage:'url('+data.item.src+')'}">
                          </v-list-tile-avatar>
                          <v-list-tile-content>
                            <v-list-tile-title v-html="data.item.name"></v-list-tile-title>
                            <v-list-tile-sub-title v-html="data.item.info"></v-list-tile-sub-title>
                          </v-list-tile-content>
                        </template>
                      </template>
                    </v-autocomplete>
                  </v-flex>

                  <v-flex xs2>
                    <v-text-field
                      v-model="combo.count"
                      label="Count"
                      :disabled="disableProductSelect"
                      @change="updateCostAndRearrangeCombosOrder"
                    ></v-text-field>
                  </v-flex>

                  <v-flex xs2>
                    <v-text-field
                      v-model="combo.droppingPriority"
                      label="Dropping Priority"
                      :disabled="disableProductSelect"
                      @change="updateCostAndRearrangeCombosOrder"
                      type="number"
                      min="0"
                      hint="Greater number drop first"
                    ></v-text-field>
                  </v-flex>

                  <v-flex xs1>
                    <v-btn icon color="red" dark fab small @click="removeCombo(combo)" :disabled="product.combos.length === 1">
                      <v-icon>remove_circle</v-icon>
                    </v-btn>
                  </v-flex>
                </v-layout>
              </transition-group>
            </v-flex>
          </v-layout>
        </v-container>
      </v-card-text>

      <v-divider v-if="!create && !loading"></v-divider>
      <v-card-text v-if="!create && !loading">
        <v-layout justify-space-around row wrap>
          <v-flex xs12 justify-center>
            <h2 class="text-xs-center">Existing Images</h2>
          </v-flex>
          <draggable v-model="imagesData" :options="{ animation: 100 }" >
            <div v-for="(image, index) in imagesData" :key="index" style="display: inline-block">
              <div :src="getImageSrc(image)" :style="getImageStyle(image)">
                <v-btn color="error" icon style="position: absolute; right: 0px;" small @click="removeImage(image)">
                  <v-icon small>mdi-delete</v-icon>
                </v-btn>
              </div>
            </div>
          </draggable>
        </v-layout>
      </v-card-text>


      <v-divider v-if="!loading"></v-divider>
      <v-card-text v-if="!loading">
        <v-btn block color="teal" dark @click="openFileDialog">
          <v-icon left>mdi-upload</v-icon>Upload Images
        </v-btn>
        <input v-show="false" type="file" accept="image/png, image/jpeg" ref="imagesDialog" @change="displayImage" multiple>
        <v-layout justify-center row>
          <div v-for="(image, index) in images" :key="index" style="display: inline-block">
            <div :src="getImageSrc(image)" :style="getImageStyle(image)"></div>
          </div>
        </v-layout>
      </v-card-text>

      <v-divider></v-divider>
      <v-card-actions class="justify-end">
        <v-btn v-if="create" @click="cancel" color="error" :disabled="loading">Cancel</v-btn>
        <v-spacer></v-spacer>
        <v-btn type="submit" color="teal" dark :disabled="loading">{{ create ? 'Create' : 'Update' }}</v-btn>
      </v-card-actions>

      <rich-text-editor-modal
        v-model="richDialog.show"
        :content="product[richDialog.target]"
        @update:content="updateProperty(product, richDialog.target, $event)"
      ></rich-text-editor-modal>
    </v-card>
  </form>
</template>

<script>
  // Components
  import ProductImage from '@/components/Product/Image'
  import RichTextEditorModal from '@/components/Common/RichTextEditorModal'

  import _ from 'lodash'

  // Mixins
  import productMixin from '@/mixins/product'

  // Services
  import comboService from '@/services/combo'

  import draggable from 'vuedraggable'

  import uuid from 'uuid/v4'

  export default {
    name: 'product-combo',

    components: {
      ProductImage,
      RichTextEditorModal,
      draggable
    },

    mixins: [
      productMixin
    ],

    props: {
      create: {
        type: Boolean,
        default: false
      },

      comboProduct: Object
    },

    data () {
      return {
        comboLoading: false,
        submitLoading: false,

        content: '',

        richDialog: {
          show: false,
          target: ''
        },

        fields: [
          {
            label: 'Name (繁體)',
            key: 'name_zh_hk',
            type: 'text',
            required: true
          },
          {
            label: 'Name (簡體)',
            key: 'name_zh_cn',
            type: 'text',
            required: true
          },
          {
            label: 'Name (EN)',
            key: 'name_en',
            type: 'text',
            required: true
          },
          {
            label: 'Description (繁體)',
            key: 'description_zh_hk',
            type: 'rich-text',
            required: false
          },
          {
            label: 'Description (簡體)',
            key: 'description_zh_cn',
            type: 'rich-text',
            required: false
          },
          {
            label: 'Description (EN)',
            key: 'description_en',
            type: 'rich-text',
            required: false
          },
          {
            label: 'SKU',
            key: 'sku',
            type: 'text',
            required: true,
            placeholder: 'Barcode number or UUID',
            readonly: true
          },
          {
            label: 'Cost',
            key: 'cost_price',
            type: 'number',
            required: true
          },
          {
            label: 'Price',
            key: 'recommended_price',
            type: 'number',
            required: true
          },
          {
            label: 'Size',
            key: 'size',
            type: 'text',
            required: false
          }
        ],

        product: {},

        thumbnail: '',
        images: [],
        imagesData: [],

        clientNameById: {},
        selectableProducts: []
      }
    },

    computed: {
      loading () {
        return this.comboLoading || this.submitLoading
      },

      productThumbnail () {
        return this.thumbnail || this.getProductThumbnail(this.product)
      },

      maxUploadImageCount () {
        if (this.create) {
          return 6
        }
        return 6 - this.imagesData.length
      },

      disableProductSelect () {
        return !this.selectableProducts.length
      }
    },

    created () {
      if (!this.create) {
        this.comboLoading = true

        // this.product = this.comboProduct
        this.product = Object.assign({}, this.comboProduct, {})
        this.product.combos = this.product.combos.map(product => {
          return {
            id: uuid(),
            product_id: product.pivot.product_id,
            count: product.pivot.count,
            droppingPriority: product.pivot.dropping_priority
          }
        })

        this.comboLoading = false
      } else {
        this.resetForm()
      }

      // Add fields' decorator
      this.fields.forEach(field => {
        this.$set(field, 'loading', false)
        this.$set(field, 'error', [])
      })

      this.imagesData = this.product.images_data
    },

    methods: {
      getProperty (object, key) {
        return _.get(object, key)
      },

      updateProperty (object, key, value) {
        if (this.create) {
          return _.set(object, key, value)
        }

        // Patch the property
        const field = this.fields.find(field => field.key === key)

        // validation for "required"
        if (field.required && (value === null || value === '')) {
          const errorMessage = field.label + ' cannot be empty'
          field.error.push(errorMessage)
          this.$toasted.error(errorMessage).goAway(5000)
          return
        }

        _.set(object, key, value)

        this.resetField(field)
      },

      resetField (field) {
        field.error = []
      },

      resetForm () {
        this.product = {
          name_zh_hk: null,
          name_zh_cn: null,
          name_en: null,
          sku: uuid(),
          recommended_price: null,
          cost_price: null,
          size: null,
          product_lists: [],
          description_zh_hk: null,
          description_zh_cn: null,
          description_en: null,
          filename: null,
          thumbnailFilename: null,
          combos: [
            {
              id: uuid(),
              product_id: null,
              count: 1,
              droppingPriority: 1
            }
          ]
        }

        this.thumbnail = ''
        this.images = []
        this.imagesData = []
      },

      cancel () {
        this.resetForm()
        this.close()
      },

      close () {
        this.$emit('close')
      },

      onDescriptionFocus (field) {
        this.richDialog.show = true
        this.richDialog.target = field
      },

      openFileDialog () {
        this.images = []
        this.$refs.imagesDialog.value = ''
        this.$refs.imagesDialog.click()
      },

      openThumbnailFileDialog () {
        this.thumbnail = ''
        this.$refs.thumbnailFileDialog.value = ''
        this.$refs.thumbnailFileDialog.click()
      },

      async onSubmit () {
        this.submitLoading = true

        const formData = new FormData()

        for (const key in this.fields) {
          const field = this.fields[key]

          // validation for "required"
          const value = this.product[field.key]
          if (field.required && (value === null || value === '')) {
            const errorMessage = field.label + ' cannot be empty'
            field.error.push(errorMessage)
            this.$toasted.error(errorMessage).goAway(5000)
            this.submitLoading = false
            return
          }

          if (value !== null) {
            if (value instanceof Array) {
              for (let v of value) {
                formData.append(`${field.key}[]`, v)
              }
            } else {
              formData.append(field.key, value)
            }
          }
        }

        // check thumbnail
        if (this.create && !this.$refs.thumbnailFileDialog.value) {
          this.$toasted.error('Thumbnail cannot be empty').goAway(5000)
          this.submitLoading = false
          return
        } else {
          if (this.$refs.thumbnailFileDialog.value) {
            formData.append('thumbnail', this.$refs.thumbnailFileDialog.files[0], 'thumbnail.png')
          }
        }

        // check product images
        if (this.create) {
          if (!this.$refs.imagesDialog.files.length) {
            this.$toasted.error('Please choose an image for product').goAway(5000)
            this.submitLoading = false
            return
          } else {
            Array.from(this.$refs.imagesDialog.files).forEach(file => {
              formData.append('images[]', file)
            })
          }
        } else {
          if (this.imagesData.length === 0 && !this.$refs.imagesDialog.files.length) {
            this.$toasted.error('Please choose an image for product').goAway(5000)
            this.submitLoading = false
            return
          } else {
            Array.from(this.$refs.imagesDialog.files).forEach(file => {
              formData.append('images[]', file)
            })

            this.imagesData.forEach(image => {
              formData.append('images_data[]', image)
            })
          }
        }

        // check if combos is empty
        if (!this.product.combos.length) {
          this.$toasted.error('Please fill out the products field').goAway(5000)
          this.submitLoading = false
          return
        }

        // check if combo products counts are valid
        // check if combo products have the same client and contract type
        let invalidCount = false
        const comboProductIds = this.product.combos.map(item => {
          if (item.count <= 0) {
            invalidCount = true
          }

          return item.product_id
        })

        // check if combo containing null product
        if (comboProductIds.includes(null)) {
          const errorMessage = 'Products in combo should not contain null'
          this.$toasted.error(errorMessage).goAway(5000)
          this.submitLoading = false
          return
        }

        // check if combo products have duplicated selection
        if (new Set(comboProductIds).size !== this.product.combos.length) {
          const errorMessage = 'Should not select a duplicate product in combo'
          this.$toasted.error(errorMessage).goAway(5000)
          this.submitLoading = false
          return
        }

        if (invalidCount) {
          const errorMessage = 'Product count must greater than 0'
          this.$toasted.error(errorMessage).goAway(5000)
          this.submitLoading = false
          return
        }

        this.product.combos.forEach((combo, index) => {
          formData.append(`combos[${index}][product_id]`, combo.product_id)
          formData.append(`combos[${index}][count]`, combo.count)
          formData.append(`combos[${index}][dropping_priority]`, combo.droppingPriority)
        })

        try {
          if (this.create) {
            const { data } = await comboService.store(formData)

            if (data.status !== 'success') {
              throw new Error(data.data.description_en)
            }

            // Notify parent to insert new row
            this.$emit('created', data.data)

            this.$toasted.success(`Created product: ${this.product.name_zh_hk}`).goAway(5000)

            // reset form in background
            this.$nextTick(() => {
              this.resetForm()
            })
          } else {
            await comboService.patch(this.product.id, formData)
            this.$toasted.success(`Updated product: ${this.product.name_zh_hk}`).goAway(5000)
            this.$router.go(0)
          }
        } catch (error) {
          this.$toasted.error('Failed to create product: ' + error.toString()).goAway(5000)

          if (error.response.data.data) {
            error.response.data.data.forEach(msg => {
              this.$toasted.error(msg).goAway(5000)
            })
          }
        } finally {
          this.submitLoading = false
        }
      },

      addCombo () {
        this.product.combos.push({
          id: uuid(),
          product_id: null,
          count: 1,
          droppingPriority: 1
        })
      },

      removeCombo (removeCombo) {
        this.product.combos = this.product.combos.filter(combo => {
          return combo.id !== removeCombo.id
        })
      },

      displayImage () {
        const files = this.$refs.imagesDialog.files

        if (files.length > this.maxUploadImageCount) {
          this.$toasted.error(`You can only choose maximum ${this.maxUploadImageCount} images for the product`).goAway(5000)
          this.$refs.imagesDialog.value = ''
          this.images = []
          return
        }

        for (let i = 0; i < files.length; i++) {
          const reader = new FileReader()
          reader.onload = () => {
            this.images.push(reader.result)
          }
          reader.onerror = error => { this.$toasted.error(error).goAway(5000) }
          reader.readAsDataURL(files[i])
        }
      },

      displayThumbnail () {
        const files = this.$refs.thumbnailFileDialog.files

        const reader = new FileReader()
        reader.onload = () => {
          this.thumbnail = reader.result
        }
        reader.onerror = error => { this.$toasted.error(error).goAway(5000) }
        reader.readAsDataURL(files[0])
      },

      getImageStyle (image) {
        return {
          position: 'relative',
          display: 'inline-block',
          height: '200px',
          width: '200px',
          backgroundImage: `url(${this.getImageSrc(image)})`,
          backgroundSize: 'contain',
          backgroundPosition: 'center',
          borderWidth: '1px',
          borderStyle: 'solid',
          borderColor: 'black',
          marginLeft: '10px',
          marginRight: '10px'
        }
      },

      getImageSrc (image) {
        if (image.match(/^data:image/)) {
          return image
        }

        return `${process.env.PRODUCT_API_HOST}/products/${this.product.product_lists[0].folder}/assets/${image}`
      },

      removeImage (selectedImage) {
        this.imagesData = this.imagesData.filter(image => {
          return image !== selectedImage
        })
      },

      updateCostAndRearrangeCombosOrder () {
        let comboCost = 0
        const selectedProductIds = this.product.combos.map(product => product.product_id)
        const selectedProductCounts = {}

        this.product.combos.forEach(product => {
          selectedProductCounts[product.product_id] = product.count
        })

        this.selectableProducts.forEach(product => {
          if (selectedProductIds.includes(product.id)) {
            comboCost += product.cost_price * selectedProductCounts[product.id]
          }
        })

        this.product.cost_price = comboCost

        this.product.combos = this.product.combos.sort((a, b) => {
          return b.droppingPriority - a.droppingPriority
        })
      }
    }

  }
</script>

<style lang="sass" scoped>
  .full-look
    height: 100%
    width: 100%
    background-size: contain
    background-repeat: no-repeat
    background-position: center

  .flip-list
    &-move
      transition: transform 0.5s

    &-enter-active
      transition: all .3s ease

    &-leave-active
      transition: all .3s ease

    &-enter, &-leave-to
      transform: translateX(30px)
      opacity: 0
</style>



// WEBPACK FOOTER //
// src/components/Product/Combo.vue